/**
 * TanStack Query hooks for `log-level-data-access`. Thin wrappers over ./api.
 * List hook accepts filters; export hook triggers CSV download.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	create,
	exportCsv,
	list,
	remove,
	logLevelDataAccessQueryKeys,
	type AuditLogFilters,
	type CreateLogLevelDataAccessBody,
	type ListLogLevelDataAccessResponse,
	type LogLevelDataAccessRow,
} from "./api";

export function useLogLevelDataAccessList(
	filters: AuditLogFilters = {},
): UseQueryResult<ListLogLevelDataAccessResponse, Error> {
	return useQuery({
		queryKey: logLevelDataAccessQueryKeys.list(filters),
		queryFn: () => list(filters),
	});
}

export function useLogLevelDataAccessCreate(): UseMutationResult<
	LogLevelDataAccessRow,
	Error,
	CreateLogLevelDataAccessBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: create,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: logLevelDataAccessQueryKeys.all });
		},
	});
}

export function useLogLevelDataAccessDelete(): UseMutationResult<
	{ removed: boolean },
	Error,
	string
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: remove,
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: logLevelDataAccessQueryKeys.all });
		},
	});
}

export function useLogLevelDataAccessExport(): UseMutationResult<string, Error, AuditLogFilters> {
	return useMutation({
		mutationFn: (filters: AuditLogFilters) => exportCsv(filters),
	});
}
